import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Copy, RefreshCw, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const KeywordDensityTool = () => {
  const [text, setText] = useState('');
  const [minLength, setMinLength] = useState(3);

  const analyzeKeywords = () => {
    if (!text.trim()) return [];

    const words = text.toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter(w => w.length >= minLength);

    const totalWords = words.length;
    const frequency = {};

    words.forEach(word => {
      frequency[word] = (frequency[word] || 0) + 1;
    });

    return Object.entries(frequency)
      .map(([word, count]) => ({
        word,
        count,
        density: ((count / totalWords) * 100).toFixed(2)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 50);
  };

  const keywords = analyzeKeywords();
  const totalWords = text.trim() ? text.trim().split(/\s+/).length : 0;

  const copyKeywords = () => {
    const csv = keywords.map(k => `${k.word},${k.count},${k.density}%`).join('\n');
    navigator.clipboard.writeText(csv);
    toast.success('Keywords copied!');
  };

  return (
    <div className="space-y-6" data-testid="keyword-density-tool">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              Content to Analyze ({totalWords} words)
            </label>
            <Textarea
              placeholder="Paste your content here to analyze keyword density..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="min-h-[300px] font-mono text-sm bg-muted/50"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              Minimum Word Length
            </label>
            <select
              value={minLength}
              onChange={(e) => setMinLength(parseInt(e.target.value))}
              className="w-full h-10 px-3 rounded-md bg-muted border border-border"
            >
              <option value={2}>2 characters</option>
              <option value={3}>3 characters</option>
              <option value={4}>4 characters</option>
              <option value={5}>5 characters</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            Keyword Analysis (Top 50)
          </label>
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="bg-muted/50 p-3 grid grid-cols-3 text-sm font-semibold border-b border-border">
              <span>Keyword</span>
              <span className="text-center">Count</span>
              <span className="text-right">Density</span>
            </div>
            <div className="max-h-[300px] overflow-auto">
              {keywords.length > 0 ? (
                keywords.map((k, i) => (
                  <div key={i} className="p-3 grid grid-cols-3 text-sm border-b border-border last:border-0 hover:bg-muted/30">
                    <span className="font-mono">{k.word}</span>
                    <span className="text-center">{k.count}</span>
                    <span className="text-right text-primary">{k.density}%</span>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-muted-foreground">
                  Enter text to analyze keywords
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={copyKeywords} disabled={keywords.length === 0}>
          <Copy className="w-4 h-4 mr-2" /> Copy Results
        </Button>
        <Button onClick={() => setText('')} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Clear
        </Button>
      </div>
    </div>
  );
};

export default KeywordDensityTool;
